/**
 * StreamContext — Phase 3 (PRI-291)
 *
 * Bridge context: PrimateVisionV2Inner owns the state and populates this context.
 * Child components consume via useStreamContext() instead of receiving props.
 *
 * Phase 3 approach: "bridge" pattern — state stays in PrimateVisionV2Inner,
 * context exposes it so prop drilling is eliminated for child components.
 * Phase 5 will move the hook calls into the provider.
 */

import { createContext, useContext } from 'react'
import type { ReactNode, Dispatch, SetStateAction } from 'react'
import type { StreamSessionState, StreamSessionActions } from '../hooks/types'
import type { ResultPill } from '../components/v2/pv2-shared'

export interface StreamContextValue extends StreamSessionState, StreamSessionActions {
  // PrimateVisionV2-specific streaming state (not part of useStreamSession)
  isStreaming: boolean
  setIsStreaming: (v: boolean) => void
  promptSent: boolean
  setPromptSent: (v: boolean) => void
  resultPills: ResultPill[]
  setResultPills: Dispatch<SetStateAction<ResultPill[]>>
  samEnabled: boolean
  setSamEnabled: (v: boolean) => void
  processOffline: boolean
  setProcessOffline: (v: boolean) => void
  /** null = no source chosen yet (first use, camera permission not granted) */
  sourceMode: 'webcam' | 'file' | null
  setSourceMode: (v: 'webcam' | 'file' | null) => void
  frozenFrameUrl: string | null
  setFrozenFrameUrl: (v: string | null) => void
  activePrompt: string
  setActivePrompt: (v: string) => void
}

const StreamContext = createContext<StreamContextValue | null>(null)

export function StreamContextProvider({
  children,
  value,
}: {
  children: ReactNode
  value: StreamContextValue
}) {
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>
}

export function useStreamContext(): StreamContextValue {
  const ctx = useContext(StreamContext)
  if (!ctx) throw new Error('useStreamContext must be used inside StreamContextProvider')
  return ctx
}

export default StreamContext
